import * as React from "react";
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { cn } from "@/shadcn/lib/utils";

import { Button } from "@/shadcn/components/ui/button";
import { XIcon } from "lucide-react";

// playerdex · registry v1 · shadcn@4.21.0

// Controlled dialogs have no trigger to return focus to, so remember what was
// focused when the dialog opened and return focus there on close.
const DialogReturnFocusContext = React.createContext(undefined);

const useReturnFocus = (open) => {
  const returnFocusRef = React.useRef(null);
  React.useLayoutEffect(() => {
    if (!open) return;
    const active = document.activeElement;
    returnFocusRef.current =
      active instanceof HTMLElement && active !== document.body ? active : null;
  }, [open]);
  return React.useCallback(() => {
    const target = returnFocusRef.current;
    return target?.isConnected ? target : true;
  }, []);
};

const Dialog = React.forwardRef(function Dialog({ open, ...props }, ref) {
  const returnFocus = useReturnFocus(open);
  return (
    <DialogReturnFocusContext.Provider value={returnFocus}>
      <DialogPrimitive.Root data-slot="dialog" ref={ref} open={open} {...props} />
    </DialogReturnFocusContext.Provider>
  );
});

const DialogTrigger = React.forwardRef(function DialogTrigger(
  { ...props },
  ref
) {
  return (
    <DialogPrimitive.Trigger data-slot="dialog-trigger" ref={ref} {...props} />
  );
});

const DialogPortal = React.forwardRef(function DialogPortal({ ...props }, ref) {
  return (
    <DialogPrimitive.Portal
      data-slot="dialog-portal"
      ref={ref}
      {...props}
    />
  );
});

const DialogClose = React.forwardRef(function DialogClose({ ...props }, ref) {
  return (
    <DialogPrimitive.Close data-slot="dialog-close" ref={ref} {...props} />
  );
});

const DialogOverlay = React.forwardRef(function DialogOverlay(
  { className, ...props },
  ref
) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      className={cn(
        "data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 bg-black/30 duration-100 supports-backdrop-filter:backdrop-blur-sm fixed inset-0 isolate z-[1300]",
        className
      )}
      ref={ref}
      {...props}
    />
  );
});

const DialogContent = React.forwardRef(function DialogContent(
  {
    className,
    children,
    showCloseButton = true,
    closeLabel = "Close",
    finalFocus,
    ...props
  },
  ref
) {
  const returnFocus = React.useContext(DialogReturnFocusContext);
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Popup
        data-slot="dialog-content"
        className={cn(
          "bg-popover text-popover-foreground data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 ring-foreground/5 dark:ring-foreground/10 grid max-h-[calc(100dvh-2rem)] max-w-[calc(100%-2rem)] gap-6 overflow-y-auto rounded-4xl *:min-w-0 has-[>[data-slot=dialog-close]]:[&_[data-slot=dialog-title]]:pr-8 p-6 text-sm shadow-xl ring-1 duration-100 sm:max-w-md fixed top-1/2 left-1/2 z-[1300] w-full -translate-x-1/2 -translate-y-1/2 outline-none",
          className
        )}
        ref={ref}
        finalFocus={finalFocus ?? returnFocus}
        {...props}
      >
        {/* First in the DOM so it is the first tab stop, like the old modals. */}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            aria-label={closeLabel}
            render={
              <Button
                variant="ghost"
                className="absolute top-4 right-4 bg-secondary"
                size="icon-sm"
              />
            }
          >
            <XIcon aria-hidden="true" />
          </DialogPrimitive.Close>
        )}
        {children}
      </DialogPrimitive.Popup>
    </DialogPortal>
  );
});

const DialogHeader = React.forwardRef(function DialogHeader(
  { className, ...props },
  ref
) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("gap-1.5 flex flex-col", className)}
      ref={ref}
      {...props}
    />
  );
});

const DialogFooter = React.forwardRef(function DialogFooter(
  { className, showCloseButton = false, children, ...props },
  ref
) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-end",
        className
      )}
      ref={ref}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close render={<Button variant="outline" />}>
          Close
        </DialogPrimitive.Close>
      )}
    </div>
  );
});

const DialogTitle = React.forwardRef(function DialogTitle(
  { className, ...props },
  ref
) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-base leading-none font-medium", className)}
      ref={ref}
      {...props}
    />
  );
});

const DialogDescription = React.forwardRef(function DialogDescription(
  { className, ...props },
  ref
) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        "text-muted-foreground *:[a]:hover:text-foreground text-sm *:[a]:underline *:[a]:underline-offset-3",
        className
      )}
      ref={ref}
      {...props}
    />
  );
});

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
